import { useState, useEffect, useRef } from 'react';
import {
  EnvelopeIcon,
  DevicePhoneMobileIcon,
  PaperAirplaneIcon,
  BeakerIcon,
  UserGroupIcon,
  DocumentTextIcon,
  ChevronDownIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  XMarkIcon,
  ClockIcon,
  MagnifyingGlassIcon,
  UserIcon,
  EyeIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { supabase } from '../lib/supabase';
import { TEMPLATE_TYPES, type EmailTemplateType } from '../lib/emailTemplates';
import { sendPushNotification, sendTestPushNotification, getPushStats, setupPushTables } from '../lib/pushNotifications';
import { toast } from 'react-hot-toast';
import EmojiPicker from '../components/EmojiPicker';

interface Player {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  category: string;
  status: string;
}

interface SendResult {
  email: string;
  name: string;
  success: boolean;
  message: string;
  attempts: number;
  timestamp: string;
}

type RecipientFilter = 'all' | 'active' | 'category' | 'selected' | 'custom';

const PUSH_ACTIONS = [
  { value: '', label: 'No action (notification only)' },
  { value: '/m/leaderboard', label: 'Go to Leaderboard' },
  { value: '/m/schedule', label: 'Go to Schedule' },
  { value: '/m/registration', label: 'Go to Registration' },
  { value: '/m/sponsors', label: 'Go to Sponsors' },
  { value: '/m/prize-breakdown', label: 'Go to Prize Breakdown' },
  { value: '/m/about', label: 'Go to About' },
  { value: '/m/history', label: 'Go to History' },
  { value: 'custom', label: 'Custom URL...' },
];

interface MessagingProps {
  tournamentId: string;
}

export default function Messaging({ tournamentId }: MessagingProps) {
  const [activeTab, setActiveTab] = useState<'email' | 'push'>('email');

  return (
    <div className="space-y-6">
      {/* Tab switcher */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="flex">
          <button
            onClick={() => setActiveTab('email')}
            className={`flex-1 py-4 px-6 text-center font-medium border-b-2 flex items-center justify-center gap-2 transition-colors ${
              activeTab === 'email'
                ? 'border-golf-orange text-golf-orange'
                : 'border-transparent text-gray-500 hover:text-golf-gray hover:border-gray-300'
            }`}
          >
            <EnvelopeIcon className="w-5 h-5" />
            Email
          </button>
          <button
            onClick={() => setActiveTab('push')}
            className={`flex-1 py-4 px-6 text-center font-medium border-b-2 flex items-center justify-center gap-2 transition-colors ${
              activeTab === 'push'
                ? 'border-golf-orange text-golf-orange'
                : 'border-transparent text-gray-500 hover:text-golf-gray hover:border-gray-300'
            }`}
          >
            <DevicePhoneMobileIcon className="w-5 h-5" />
            Push Notifications
          </button>
        </div>
      </div>

      {activeTab === 'email' ? (
        <EmailComposer tournamentId={tournamentId} />
      ) : (
        <PushComposer />
      )}
    </div>
  );
}

function EmailComposer({ tournamentId }: { tournamentId: string }) {
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplateType | null>(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [recipientFilter, setRecipientFilter] = useState<RecipientFilter>('all');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [customEmails, setCustomEmails] = useState('');
  const [players, setPlayers] = useState<Player[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [sending, setSending] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [showTemplateDropdown, setShowTemplateDropdown] = useState(false);
  const [sendResults, setSendResults] = useState<SendResult[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [placeholders, setPlaceholders] = useState<Record<string, string>>({});
  const [selectedPlayerIds, setSelectedPlayerIds] = useState<Set<string>>(new Set());
  const [playerSearch, setPlayerSearch] = useState('');
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const bodyTextareaRef = useRef<HTMLTextAreaElement>(null);

  const insertEmojiInBody = (emoji: string) => {
    const textarea = bodyTextareaRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newBody = body.slice(0, start) + emoji + body.slice(end);
      setBody(newBody);
      requestAnimationFrame(() => {
        textarea.focus();
        textarea.selectionStart = textarea.selectionEnd = start + emoji.length;
      });
    } else {
      setBody(body + emoji);
    }
  };

  useEffect(() => {
    fetchPlayers();
  }, [tournamentId]);

  useEffect(() => {
    if (selectedTemplate) {
      setSubject(selectedTemplate.defaultSubject);
      const initial: Record<string, string> = {};
      selectedTemplate.placeholders.forEach(p => {
        initial[p.key] = p.example;
      });
      setPlaceholders(initial);
    }
  }, [selectedTemplate]);

  const fetchPlayers = async () => {
    const { data } = await supabase
      .from('players')
      .select('id, first_name, last_name, email, category, status')
      .eq('tournament_id', tournamentId);

    if (data) {
      setPlayers(data);
      const cats = [...new Set(data.map(p => p.category).filter(Boolean))];
      setCategories(cats);
    }
  };

  const getRecipients = (): { email: string; name: string }[] => {
    switch (recipientFilter) {
      case 'all':
        return players.map(p => ({ email: p.email, name: `${p.first_name} ${p.last_name}` }));
      case 'active':
        return players
          .filter(p => p.status?.toLowerCase() === 'active')
          .map(p => ({ email: p.email, name: `${p.first_name} ${p.last_name}` }));
      case 'category':
        return players
          .filter(p => p.category === selectedCategory)
          .map(p => ({ email: p.email, name: `${p.first_name} ${p.last_name}` }));
      case 'selected':
        return players
          .filter(p => selectedPlayerIds.has(p.id))
          .map(p => ({ email: p.email, name: `${p.first_name} ${p.last_name}` }));
      case 'custom':
        return customEmails
          .split(/[,;\n]+/)
          .map(e => e.trim())
          .filter(e => e.includes('@'))
          .map(e => ({ email: e, name: '' }));
      default:
        return [];
    }
  };

  const recipientCount = getRecipients().length;

  const MAX_AUTO_RETRIES = 4;
  const BASE_BACKOFF_MS = 2000;

  const sendEmail = async (to: string, recipientName = '', isTest = false, attemptNumber = 1): Promise<SendResult> => {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token ?? import.meta.env.VITE_SUPABASE_ANON_KEY;

    const nameParts = recipientName.trim().split(/\s+/);
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    const recipientPlaceholders = { ...placeholders, firstName, lastName };

    const payload: Record<string, unknown> = {
      to,
      placeholders: recipientPlaceholders,
    };

    if (selectedTemplate) {
      payload.emailType = selectedTemplate.id;
      payload.subjectOverride = subject;
    } else {
      payload.emailType = 'announcement';
      payload.placeholders = { ...recipientPlaceholders, headline: subject, message: body };
      payload.subjectOverride = subject;
    }

    for (let attempt = attemptNumber; attempt <= attemptNumber + MAX_AUTO_RETRIES - 1; attempt++) {
      try {
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-email`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
            },
            body: JSON.stringify(payload),
          }
        );

        if (!response.ok) {
          const text = await response.text();
          const is451 = text.includes('451');
          if (is451 && attempt < attemptNumber + MAX_AUTO_RETRIES - 1) {
            const backoff = BASE_BACKOFF_MS * Math.pow(2, attempt - attemptNumber);
            await new Promise(resolve => setTimeout(resolve, backoff));
            continue;
          }
          return { email: to, name: recipientName, success: false, message: text.slice(0, 200), attempts: attempt, timestamp: new Date().toISOString() };
        }

        const result = await response.json();
        if (!result.success && result.message?.includes('451') && attempt < attemptNumber + MAX_AUTO_RETRIES - 1) {
          const backoff = BASE_BACKOFF_MS * Math.pow(2, attempt - attemptNumber);
          await new Promise(resolve => setTimeout(resolve, backoff));
          continue;
        }
        return { email: to, name: recipientName, success: result.success, message: result.message || 'Sent', attempts: attempt, timestamp: new Date().toISOString() };
      } catch (err: any) {
        if (attempt < attemptNumber + MAX_AUTO_RETRIES - 1) {
          const backoff = BASE_BACKOFF_MS * Math.pow(2, attempt - attemptNumber);
          await new Promise(resolve => setTimeout(resolve, backoff));
          continue;
        }
        return { email: to, name: recipientName, success: false, message: err.message || 'Unknown error', attempts: attempt, timestamp: new Date().toISOString() };
      }
    }

    return { email: to, name: recipientName, success: false, message: 'Max retries exceeded', attempts: attemptNumber + MAX_AUTO_RETRIES - 1, timestamp: new Date().toISOString() };
  };

  const handleSendTest = async () => {
    if (!testEmail.trim()) {
      toast.error('Enter a test email address');
      return;
    }
    setSending(true);
    const result = await sendEmail(testEmail.trim(), '', true);
    if (result.success) {
      toast.success(`Test email sent to ${testEmail}`);
    } else {
      toast.error(`Failed: ${result.message}`);
    }
    setSending(false);
  };

  const handleSendAll = async () => {
    const recipients = getRecipients();
    if (recipients.length === 0) {
      toast.error('No recipients selected');
      return;
    }

    if (!subject.trim() && !selectedTemplate) {
      toast.error('Please enter a subject');
      return;
    }

    if (scheduleEnabled) {
      if (!scheduleDate || !scheduleTime) {
        toast.error('Please set a schedule date and time');
        return;
      }
      const scheduledAt = new Date(`${scheduleDate}T${scheduleTime}`);
      if (scheduledAt <= new Date()) {
        toast.error('Scheduled time must be in the future');
        return;
      }

      setSending(true);
      const { error } = await supabase.from('sent_messages').insert({
        type: 'email',
        subject: subject || selectedTemplate?.defaultSubject || '',
        body: body || '',
        template_id: selectedTemplate?.id || null,
        recipients: recipients,
        recipient_count: recipients.length,
        status: 'scheduled',
        sent_at: scheduledAt.toISOString(),
        is_test: false,
      });

      if (error) {
        toast.error('Failed to schedule message');
      } else {
        toast.success(`Message scheduled for ${scheduledAt.toLocaleString()}`);
      }
      setSending(false);
      return;
    }

    setSending(true);
    setSendResults([]);
    setShowResults(true);

    const BATCH_SIZE = 3;
    const DELAY_BETWEEN_BATCHES_MS = 3000;
    const DELAY_BETWEEN_EMAILS_MS = 800;

    const results: SendResult[] = [];
    for (let i = 0; i < recipients.length; i += BATCH_SIZE) {
      const batch = recipients.slice(i, i + BATCH_SIZE);

      const batchResults = await Promise.all(
        batch.map((recipient, idx) =>
          new Promise<SendResult>(resolve =>
            setTimeout(async () => {
              const result = await sendEmail(recipient.email, recipient.name);
              resolve(result);
            }, idx * DELAY_BETWEEN_EMAILS_MS)
          )
        )
      );

      results.push(...batchResults);
      setSendResults([...results]);

      if (i + BATCH_SIZE < recipients.length) {
        await new Promise(resolve => setTimeout(resolve, DELAY_BETWEEN_BATCHES_MS));
      }
    }

    const successCount = results.filter(r => r.success).length;
    const failCount = results.filter(r => !r.success).length;

    if (failCount === 0) {
      toast.success(`All ${successCount} emails sent successfully`);
    } else {
      toast(`${successCount} sent, ${failCount} failed`, { icon: '!' });
    }

    setSending(false);
  };

  const handleRetryFailed = async () => {
    const failed = sendResults.filter(r => !r.success);
    if (failed.length === 0) {
      toast.error('No failed emails to retry');
      return;
    }

    setSending(true);

    const BATCH_SIZE = 3;
    const DELAY_BETWEEN_BATCHES_MS = 2000;
    const DELAY_BETWEEN_EMAILS_MS = 500;

    const updatedResults = [...sendResults];

    for (let i = 0; i < failed.length; i += BATCH_SIZE) {
      const batch = failed.slice(i, i + BATCH_SIZE);

      const batchResults = await Promise.all(
        batch.map((failedResult, idx) =>
          new Promise<SendResult>(resolve =>
            setTimeout(async () => {
              const result = await sendEmail(
                failedResult.email,
                failedResult.name,
                false,
                failedResult.attempts + 1
              );
              resolve(result);
            }, idx * DELAY_BETWEEN_EMAILS_MS)
          )
        )
      );

      for (const retryResult of batchResults) {
        const idx = updatedResults.findIndex(r => r.email === retryResult.email && !r.success);
        if (idx !== -1) {
          updatedResults[idx] = retryResult;
        }
      }
      setSendResults([...updatedResults]);

      if (i + BATCH_SIZE < failed.length) {
        await new Promise(resolve => setTimeout(resolve, DELAY_BETWEEN_BATCHES_MS));
      }
    }

    const newSuccessCount = updatedResults.filter(r => r.success).length;
    const newFailCount = updatedResults.filter(r => !r.success).length;

    if (newFailCount === 0) {
      toast.success(`All retries successful! ${newSuccessCount} total sent.`);
    } else {
      toast(`Retry complete: ${newSuccessCount} sent, ${newFailCount} still failed`, { icon: '!' });
    }

    setSending(false);
  };

  const handleRetrySingle = async (result: SendResult) => {
    setSending(true);
    const retryResult = await sendEmail(result.email, result.name, false, result.attempts + 1);
    const updatedResults = sendResults.map(r =>
      r.email === result.email && !r.success ? retryResult : r
    );
    setSendResults(updatedResults);

    if (retryResult.success) {
      toast.success(`Retry successful for ${result.email}`);
    } else {
      toast.error(`Retry failed for ${result.email}`);
    }
    setSending(false);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Left column - Compose */}
      <div className="lg:col-span-2 space-y-4">
        {/* Template selector */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <label className="block text-sm font-medium text-gray-700 mb-2">Email Template</label>
          <div className="relative">
            <button
              onClick={() => setShowTemplateDropdown(!showTemplateDropdown)}
              className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-left hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center gap-2">
                <DocumentTextIcon className="w-4 h-4 text-gray-400" />
                <span className={selectedTemplate ? 'text-gray-800 font-medium' : 'text-gray-400'}>
                  {selectedTemplate ? selectedTemplate.name : 'Select a template (optional)'}
                </span>
              </div>
              <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${showTemplateDropdown ? 'rotate-180' : ''}`} />
            </button>

            {showTemplateDropdown && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                <button
                  onClick={() => { setSelectedTemplate(null); setShowTemplateDropdown(false); }}
                  className="w-full px-4 py-2.5 text-left text-sm text-gray-500 hover:bg-gray-50 border-b border-gray-100"
                >
                  No template (custom message)
                </button>
                {TEMPLATE_TYPES.map(template => (
                  <button
                    key={template.id}
                    onClick={() => { setSelectedTemplate(template); setShowTemplateDropdown(false); }}
                    className={`w-full px-4 py-2.5 text-left hover:bg-gray-50 ${selectedTemplate?.id === template.id ? 'bg-golf-orange/5' : ''}`}
                  >
                    <p className="text-sm font-medium text-gray-800">{template.name}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{template.description}</p>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Subject & Body */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Subject</label>
            <input
              type="text"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              placeholder="Email subject line..."
              className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange"
            />
          </div>

          {selectedTemplate ? (
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">Template Placeholders</label>
              <div className="grid grid-cols-1 gap-3">
                {selectedTemplate.placeholders.map(ph => (
                  <div key={ph.key}>
                    <label className="block text-xs text-gray-500 mb-1">{ph.label}</label>
                    <textarea
                      value={placeholders[ph.key] || ''}
                      onChange={e => setPlaceholders({ ...placeholders, [ph.key]: e.target.value })}
                      placeholder={ph.example}
                      rows={2}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange resize-y"
                    />
                  </div>
                ))}
              </div>
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                <p className="text-xs text-gray-400 mb-1">Template preview uses the branded layout with your configured placeholders.</p>
              </div>
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-sm font-medium text-gray-700">Message Body</label>
                <EmojiPicker onSelect={insertEmojiInBody} />
              </div>
              <textarea
                ref={bodyTextareaRef}
                value={body}
                onChange={e => setBody(e.target.value)}
                placeholder="Compose your message here... (HTML supported)"
                rows={20}
                className="w-full px-4 py-4 border border-gray-200 rounded-lg text-sm font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange min-h-[400px]"
              />
              <p className="text-xs text-gray-400 mt-1">HTML is supported. The message will be wrapped in the branded email layout.</p>
            </div>
          )}
        </div>

        {/* Test send */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center gap-2 mb-3">
            <BeakerIcon className="w-4 h-4 text-golf-green" />
            <h3 className="text-sm font-semibold text-gray-700">Send Test Email</h3>
          </div>
          <div className="flex gap-2">
            <input
              type="email"
              value={testEmail}
              onChange={e => setTestEmail(e.target.value)}
              placeholder="test@example.com"
              className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange"
            />
            <button
              onClick={handleSendTest}
              disabled={sending}
              className="px-4 py-2.5 bg-gray-700 text-white rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              <BeakerIcon className="w-4 h-4" />
              {sending ? 'Sending...' : 'Send Test'}
            </button>
          </div>
        </div>
      </div>

      {/* Right column - Recipients & Send */}
      <div className="space-y-4">
        {/* Recipients */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center gap-2 mb-4">
            <UserGroupIcon className="w-4 h-4 text-golf-green" />
            <h3 className="text-sm font-semibold text-gray-700">Recipients</h3>
          </div>

          <div className="space-y-2">
            {[
              { value: 'all' as const, label: 'All players', count: players.length },
              { value: 'active' as const, label: 'Active players only', count: players.filter(p => p.status?.toLowerCase() === 'active').length },
              { value: 'category' as const, label: 'By category', count: null },
              { value: 'selected' as const, label: 'Select players', count: selectedPlayerIds.size || null },
              { value: 'custom' as const, label: 'Custom list', count: null },
            ].map(option => (
              <label
                key={option.value}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors ${
                  recipientFilter === option.value
                    ? 'border-golf-orange bg-golf-orange/5'
                    : 'border-gray-100 hover:border-gray-200'
                }`}
              >
                <input
                  type="radio"
                  name="recipientFilter"
                  value={option.value}
                  checked={recipientFilter === option.value}
                  onChange={() => setRecipientFilter(option.value)}
                  className="text-golf-orange focus:ring-golf-orange"
                />
                <span className="text-sm text-gray-700 flex-1">{option.label}</span>
                {option.count !== null && (
                  <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{option.count}</span>
                )}
              </label>
            ))}
          </div>

          {recipientFilter === 'category' && (
            <div className="mt-3">
              <select
                value={selectedCategory}
                onChange={e => setSelectedCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-golf-orange/30"
              >
                <option value="">Select category...</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          )}

          {recipientFilter === 'selected' && (
            <div className="mt-3 space-y-2">
              <div className="relative">
                <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={playerSearch}
                  onChange={e => setPlayerSearch(e.target.value)}
                  placeholder="Search players..."
                  className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-golf-orange/30"
                />
              </div>
              <div className="flex items-center justify-between px-1">
                <button
                  onClick={() => {
                    const filtered = players.filter(p => {
                      if (!playerSearch) return true;
                      const q = playerSearch.toLowerCase();
                      return `${p.first_name} ${p.last_name}`.toLowerCase().includes(q) || p.email.toLowerCase().includes(q);
                    });
                    setSelectedPlayerIds(new Set(filtered.map(p => p.id)));
                  }}
                  className="text-xs text-golf-orange hover:underline"
                >
                  Select all
                </button>
                <button
                  onClick={() => setSelectedPlayerIds(new Set())}
                  className="text-xs text-gray-400 hover:underline"
                >
                  Clear
                </button>
              </div>
              <div className="max-h-48 overflow-y-auto border border-gray-100 rounded-lg divide-y divide-gray-50">
                {players
                  .filter(p => {
                    if (!playerSearch) return true;
                    const q = playerSearch.toLowerCase();
                    return `${p.first_name} ${p.last_name}`.toLowerCase().includes(q) || p.email.toLowerCase().includes(q);
                  })
                  .map(player => (
                    <label
                      key={player.id}
                      className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedPlayerIds.has(player.id)}
                        onChange={() => {
                          const next = new Set(selectedPlayerIds);
                          if (next.has(player.id)) {
                            next.delete(player.id);
                          } else {
                            next.add(player.id);
                          }
                          setSelectedPlayerIds(next);
                        }}
                        className="rounded text-golf-orange focus:ring-golf-orange"
                      />
                      <UserIcon className="w-3.5 h-3.5 text-gray-300 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-700 truncate">{player.first_name} {player.last_name}</p>
                        <p className="text-[10px] text-gray-400 truncate">{player.email}</p>
                      </div>
                    </label>
                  ))}
                {players.filter(p => {
                  if (!playerSearch) return true;
                  const q = playerSearch.toLowerCase();
                  return `${p.first_name} ${p.last_name}`.toLowerCase().includes(q) || p.email.toLowerCase().includes(q);
                }).length === 0 && (
                  <p className="text-xs text-gray-400 text-center py-3">No players found</p>
                )}
              </div>
            </div>
          )}

          {recipientFilter === 'custom' && (
            <div className="mt-3">
              <textarea
                value={customEmails}
                onChange={e => setCustomEmails(e.target.value)}
                placeholder="Enter email addresses (comma, semicolon, or newline separated)"
                rows={4}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-golf-orange/30 resize-y"
              />
            </div>
          )}

          <div className="mt-4 pt-3 border-t border-gray-100">
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-400">Total recipients</span>
              <span className="text-sm font-bold text-golf-green">{recipientCount}</span>
            </div>
          </div>
        </div>

        {/* Schedule */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={scheduleEnabled}
              onChange={e => setScheduleEnabled(e.target.checked)}
              className="rounded text-golf-orange focus:ring-golf-orange"
            />
            <ClockIcon className="w-4 h-4 text-gray-400" />
            <span className="text-sm font-medium text-gray-700">Schedule for later</span>
          </label>

          {scheduleEnabled && (
            <div className="mt-3 grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Date</label>
                <input
                  type="date"
                  value={scheduleDate}
                  onChange={e => setScheduleDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-golf-orange/30"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Time</label>
                <input
                  type="time"
                  value={scheduleTime}
                  onChange={e => setScheduleTime(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-golf-orange/30"
                />
              </div>
            </div>
          )}
        </div>

        {/* Preview & Send buttons */}
        <div className="flex gap-2">
          <button
            onClick={() => setShowPreview(true)}
            disabled={recipientCount === 0 || (!subject.trim() && !selectedTemplate)}
            className="flex-1 py-3.5 bg-gray-100 text-gray-700 rounded-xl font-semibold text-sm hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            <EyeIcon className="w-4 h-4" />
            Preview
          </button>
          <button
            onClick={handleSendAll}
            disabled={sending || recipientCount === 0}
            className="flex-[2] py-3.5 bg-golf-orange text-white rounded-xl font-semibold text-sm hover:bg-golf-orange/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 shadow-sm"
          >
            {scheduleEnabled ? <ClockIcon className="w-4 h-4" /> : <PaperAirplaneIcon className="w-4 h-4" />}
            {sending
              ? 'Processing...'
              : scheduleEnabled
                ? `Schedule for ${recipientCount} recipient${recipientCount !== 1 ? 's' : ''}`
                : `Send to ${recipientCount} recipient${recipientCount !== 1 ? 's' : ''}`
            }
          </button>
        </div>

        {/* Send results */}
        {showResults && sendResults.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Send Report</h4>
              <button onClick={() => setShowResults(false)} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>

            {/* Summary stats */}
            <div className="grid grid-cols-3 gap-2 mb-3">
              <div className="bg-green-50 rounded-lg p-2 text-center">
                <p className="text-lg font-bold text-green-700">{sendResults.filter(r => r.success).length}</p>
                <p className="text-[10px] text-green-600 font-medium">Sent</p>
              </div>
              <div className="bg-red-50 rounded-lg p-2 text-center">
                <p className="text-lg font-bold text-red-700">{sendResults.filter(r => !r.success).length}</p>
                <p className="text-[10px] text-red-600 font-medium">Failed</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-2 text-center">
                <p className="text-lg font-bold text-gray-700">{sendResults.length}</p>
                <p className="text-[10px] text-gray-600 font-medium">Total</p>
              </div>
            </div>

            {/* Retry all failed button */}
            {sendResults.some(r => !r.success) && (
              <button
                onClick={handleRetryFailed}
                disabled={sending}
                className="w-full mb-3 py-2 px-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-xs font-semibold hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                <ArrowPathIcon className="w-3.5 h-3.5" />
                {sending ? 'Retrying...' : `Retry All Failed (${sendResults.filter(r => !r.success).length})`}
              </button>
            )}

            {/* Individual results */}
            <div className="max-h-52 overflow-y-auto space-y-1.5">
              {sendResults.filter(r => !r.success).map((result, i) => (
                <div key={`fail-${i}`} className="flex items-center gap-2 text-xs bg-red-50/50 rounded-lg px-2.5 py-2">
                  <ExclamationCircleIcon className="w-4 h-4 text-red-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-gray-700 truncate">{result.email}</p>
                    <p className="text-red-400 truncate text-[10px]" title={result.message}>
                      {result.message.slice(0, 60)}{result.attempts > 1 ? ` (attempt ${result.attempts})` : ''}
                    </p>
                  </div>
                  <button
                    onClick={() => handleRetrySingle(result)}
                    disabled={sending}
                    className="flex-shrink-0 p-1 rounded hover:bg-red-100 disabled:opacity-50 transition-colors"
                    title="Retry this email"
                  >
                    <ArrowPathIcon className="w-3.5 h-3.5 text-red-600" />
                  </button>
                </div>
              ))}
              {sendResults.filter(r => r.success).map((result, i) => (
                <div key={`ok-${i}`} className="flex items-center gap-2 text-xs px-2.5 py-1.5">
                  <CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span className="text-gray-600 truncate flex-1">{result.email}</span>
                  {result.attempts > 1 && (
                    <span className="text-[10px] text-gray-400">attempt {result.attempts}</span>
                  )}
                </div>
              ))}
            </div>

            {/* Progress indicator while sending */}
            {sending && (
              <div className="mt-3 pt-2 border-t border-gray-100">
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" />
                  <span>Processing... {sendResults.length} of {recipientCount} complete</span>
                </div>
                <div className="mt-1.5 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-golf-orange rounded-full transition-all duration-300"
                    style={{ width: `${(sendResults.length / Math.max(recipientCount, 1)) * 100}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <EyeIcon className="w-5 h-5 text-gray-500" />
                <h3 className="text-lg font-semibold text-gray-800">Message Preview</h3>
              </div>
              <button
                onClick={() => setShowPreview(false)}
                className="p-1 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <XMarkIcon className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {/* Meta info */}
              <div className="space-y-2 text-sm">
                <div className="flex items-start gap-3">
                  <span className="text-gray-400 w-20 flex-shrink-0">To:</span>
                  <span className="text-gray-700">
                    {recipientFilter === 'all' && 'All players'}
                    {recipientFilter === 'active' && 'Active players only'}
                    {recipientFilter === 'category' && `Category: ${selectedCategory}`}
                    {recipientFilter === 'selected' && `${selectedPlayerIds.size} selected player${selectedPlayerIds.size !== 1 ? 's' : ''}`}
                    {recipientFilter === 'custom' && 'Custom list'}
                    {' '}({recipientCount} recipient{recipientCount !== 1 ? 's' : ''})
                  </span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-gray-400 w-20 flex-shrink-0">Subject:</span>
                  <span className="text-gray-700 font-medium">{subject || selectedTemplate?.defaultSubject || '(no subject)'}</span>
                </div>
                {selectedTemplate && (
                  <div className="flex items-start gap-3">
                    <span className="text-gray-400 w-20 flex-shrink-0">Template:</span>
                    <span className="text-gray-700">{selectedTemplate.name}</span>
                  </div>
                )}
                {scheduleEnabled && scheduleDate && scheduleTime && (
                  <div className="flex items-start gap-3">
                    <span className="text-gray-400 w-20 flex-shrink-0">Scheduled:</span>
                    <span className="text-gray-700">
                      {new Date(`${scheduleDate}T${scheduleTime}`).toLocaleString(undefined, {
                        weekday: 'short',
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  </div>
                )}
              </div>

              {/* Divider */}
              <div className="border-t border-gray-100" />

              {/* Body preview */}
              <div className="bg-gray-50 rounded-xl border border-gray-100 p-5">
                <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-3">Email Body</p>
                {body ? (
                  <div
                    className="prose prose-sm max-w-none text-gray-700"
                    dangerouslySetInnerHTML={{ __html: body }}
                  />
                ) : selectedTemplate ? (
                  <p className="text-sm text-gray-500 italic">This message uses the "{selectedTemplate.name}" template with the configured placeholders.</p>
                ) : (
                  <p className="text-sm text-gray-400 italic">(empty body)</p>
                )}
              </div>

              {/* Recipient list preview */}
              {recipientFilter === 'selected' && selectedPlayerIds.size > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">Selected Players</p>
                  <div className="flex flex-wrap gap-1.5">
                    {players
                      .filter(p => selectedPlayerIds.has(p.id))
                      .slice(0, 20)
                      .map(p => (
                        <span key={p.id} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-md text-xs text-gray-600">
                          <UserIcon className="w-3 h-3" />
                          {p.first_name} {p.last_name}
                        </span>
                      ))}
                    {selectedPlayerIds.size > 20 && (
                      <span className="inline-flex items-center px-2 py-1 text-xs text-gray-400">
                        +{selectedPlayerIds.size - 20} more
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50 rounded-b-2xl">
              <button
                onClick={() => setShowPreview(false)}
                className="flex-1 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                Edit
              </button>
              <button
                onClick={() => {
                  setShowPreview(false);
                  handleSendAll();
                }}
                disabled={sending}
                className="flex-[2] py-2.5 bg-golf-orange text-white rounded-lg text-sm font-medium hover:bg-golf-orange/90 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
              >
                {scheduleEnabled ? <ClockIcon className="w-4 h-4" /> : <PaperAirplaneIcon className="w-4 h-4" />}
                {scheduleEnabled ? 'Schedule' : 'Send Now'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

type TargetAudience = 'all' | 'platform_ios' | 'platform_android' | 'platform_web';

const TARGET_OPTIONS: { value: TargetAudience; label: string; description: string }[] = [
  { value: 'all', label: 'All Devices', description: 'Send to every registered device' },
  { value: 'platform_ios', label: 'iOS Only', description: 'Send only to iPhone/iPad devices' },
  { value: 'platform_android', label: 'Android Only', description: 'Send only to Android devices' },
  { value: 'platform_web', label: 'Web Only', description: 'Send only to web push subscribers' },
];

function PushComposer() {
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [action, setAction] = useState('');
  const [customUrl, setCustomUrl] = useState('');
  const [sending, setSending] = useState(false);
  const [testDeviceId, setTestDeviceId] = useState('');
  const [targetAudience, setTargetAudience] = useState<TargetAudience>('all');
  const [logoUrl, setLogoUrl] = useState<string | null>('https://wjwcuakqeesxnbjznajh.supabase.co/storage/v1/object/public/images/ibomopen2026.png');
  const [stats, setStats] = useState<{ total_devices: number; ios_devices: number; android_devices: number; web_devices: number } | null>(null);
  const [recentNotifications, setRecentNotifications] = useState<Array<{ id: string; title: string; body: string; devices_sent: number; sent_at: string }>>([]);
  const [loadingStats, setLoadingStats] = useState(true);
  const pushMessageRef = useRef<HTMLTextAreaElement>(null);

  const insertEmojiInPush = (emoji: string) => {
    const textarea = pushMessageRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newMsg = message.slice(0, start) + emoji + message.slice(end);
      if (newMsg.length <= 240) {
        setMessage(newMsg);
        requestAnimationFrame(() => {
          textarea.focus();
          textarea.selectionStart = textarea.selectionEnd = start + emoji.length;
        });
      }
    } else {
      const newMsg = message + emoji;
      if (newMsg.length <= 240) setMessage(newMsg);
    }
  };

  useEffect(() => {
    loadStats();
    loadLogo();
  }, []);

  const loadLogo = async () => {
    try {
      const { data } = await supabase
        .from('logos')
        .select('url')
        .eq('type', 'primary')
        .eq('location', 'header')
        .eq('is_active', true)
        .limit(1)
        .maybeSingle();
      if (data?.url) setLogoUrl(data.url);
    } catch {}
  };

  const loadStats = async () => {
    setLoadingStats(true);
    try {
      const result = await getPushStats();
      if (result.success) {
        setStats(result.stats);
        setRecentNotifications(result.recent_notifications || []);
      } else {
        // Fallback: query device_tokens directly
        const { count: total } = await supabase.from('device_tokens').select('id', { count: 'exact', head: true }).eq('is_active', true);
        const { count: ios } = await supabase.from('device_tokens').select('id', { count: 'exact', head: true }).eq('is_active', true).eq('platform', 'ios');
        const { count: android } = await supabase.from('device_tokens').select('id', { count: 'exact', head: true }).eq('is_active', true).eq('platform', 'android');
        setStats({
          total_devices: total || 0,
          ios_devices: ios || 0,
          android_devices: android || 0,
          web_devices: (total || 0) - (ios || 0) - (android || 0),
        });
      }
    } catch {
      // Fallback: query device_tokens directly
      try {
        const { count: total } = await supabase.from('device_tokens').select('id', { count: 'exact', head: true }).eq('is_active', true);
        const { count: ios } = await supabase.from('device_tokens').select('id', { count: 'exact', head: true }).eq('is_active', true).eq('platform', 'ios');
        const { count: android } = await supabase.from('device_tokens').select('id', { count: 'exact', head: true }).eq('is_active', true).eq('platform', 'android');
        setStats({
          total_devices: total || 0,
          ios_devices: ios || 0,
          android_devices: android || 0,
          web_devices: (total || 0) - (ios || 0) - (android || 0),
        });
      } catch {
        // Table doesn't exist yet
      }
    } finally {
      setLoadingStats(false);
    }
  };

  const actionUrl = action === 'custom' ? customUrl : action;

  const handleSendTest = async () => {
    if (!title.trim() || !message.trim()) {
      toast.error('Please enter a title and message');
      return;
    }
    if (!testDeviceId.trim()) {
      toast.error('Please enter a device token or email');
      return;
    }
    setSending(true);
    try {
      const result = await sendTestPushNotification({
        title: title.trim(),
        body: message.trim(),
        action_url: actionUrl || undefined,
        target_value: testDeviceId.trim(),
      });
      if (result.success) {
        toast.success(result.message || 'Test notification sent');
        loadStats();
      } else {
        toast.error(result.error || 'Failed to send test notification');
      }
    } catch {
      toast.error('Failed to send test notification');
    } finally {
      setSending(false);
    }
  };

  const handleSendAll = async () => {
    if (!title.trim() || !message.trim()) {
      toast.error('Please enter a title and message');
      return;
    }
    setSending(true);
    try {
      const params: { title: string; body: string; action_url?: string; target_type?: 'all' | 'single' | 'platform'; target_value?: string } = {
        title: title.trim(),
        body: message.trim(),
        action_url: actionUrl || undefined,
      };

      if (targetAudience === 'all') {
        params.target_type = 'all';
      } else {
        params.target_type = 'platform';
        params.target_value = targetAudience.replace('platform_', '');
      }

      const result = await sendPushNotification(params);
      if (result.success) {
        toast.success(result.message || 'Push notification sent');
        setTitle('');
        setMessage('');
        setAction('');
        loadStats();
      } else {
        toast.error(result.error || 'Failed to send notification');
      }
    } catch {
      toast.error('Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Left column - Compose */}
      <div className="lg:col-span-2 space-y-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Notification Title</label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="e.g. Round 2 Starting Soon!"
              maxLength={65}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange"
            />
            <p className="text-xs text-gray-400 mt-1">{title.length}/65 characters</p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-sm font-medium text-gray-700">Message</label>
              <EmojiPicker onSelect={insertEmojiInPush} />
            </div>
            <textarea
              ref={pushMessageRef}
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="Enter notification message..."
              rows={4}
              maxLength={240}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange resize-y"
            />
            <p className="text-xs text-gray-400 mt-1">{message.length}/240 characters</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Tap Action</label>
            <select
              value={action}
              onChange={e => setAction(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange bg-white"
            >
              {PUSH_ACTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <p className="text-xs text-gray-400 mt-1">Where users land when they tap the notification</p>
          </div>

          {action === 'custom' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Custom URL</label>
              <input
                type="text"
                value={customUrl}
                onChange={e => setCustomUrl(e.target.value)}
                placeholder="/m/custom-page or https://..."
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Target Audience</label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {TARGET_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setTargetAudience(opt.value)}
                  className={`px-3 py-2.5 rounded-lg border text-sm font-medium transition-all ${
                    targetAudience === opt.value
                      ? 'border-golf-orange bg-golf-orange/5 text-golf-orange ring-1 ring-golf-orange/30'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-400 mt-1.5">
              {TARGET_OPTIONS.find(o => o.value === targetAudience)?.description}
              {stats && targetAudience !== 'all' && (
                <span className="ml-1 text-gray-500 font-medium">
                  ({targetAudience === 'platform_ios' ? stats.ios_devices :
                    targetAudience === 'platform_android' ? stats.android_devices :
                    stats.web_devices} devices)
                </span>
              )}
              {stats && targetAudience === 'all' && (
                <span className="ml-1 text-gray-500 font-medium">
                  ({stats.total_devices} devices)
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Test send */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center gap-2 mb-3">
            <BeakerIcon className="w-4 h-4 text-golf-green" />
            <h3 className="text-sm font-semibold text-gray-700">Send Test Notification</h3>
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={testDeviceId}
              onChange={e => setTestDeviceId(e.target.value)}
              placeholder="Device token or user email..."
              className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange"
            />
            <button
              onClick={handleSendTest}
              disabled={sending}
              className="px-4 py-2.5 bg-gray-700 text-white rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              <BeakerIcon className="w-4 h-4" />
              {sending ? 'Sending...' : 'Test'}
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-2">Paste a real FCM registration token from the native app or enter a user email whose device has been registered.</p>
        </div>
      </div>

      {/* Right column - Preview & Send */}
      <div className="space-y-4">
        {/* Preview */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">Preview</h3>

          <div className="bg-gray-900 rounded-2xl p-4 shadow-inner">
            <div className="bg-white rounded-xl p-3 shadow-sm">
              <div className="flex items-start gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {logoUrl ? (
                    <img src={logoUrl} alt="App" className="w-full h-full object-contain p-0.5" />
                  ) : (
                    <span className="text-white text-xs font-bold">IO</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold text-gray-900 truncate">Ibom Open</p>
                    <span className="text-[10px] text-gray-400">now</span>
                  </div>
                  <p className="text-xs font-medium text-gray-800 mt-0.5 truncate">
                    {title || 'Notification title'}
                  </p>
                  <p className="text-[11px] text-gray-500 mt-0.5 line-clamp-2">
                    {message || 'Notification message will appear here...'}
                  </p>
                </div>
              </div>
            </div>

            {actionUrl && (
              <div className="mt-2 px-1">
                <p className="text-[10px] text-gray-400">
                  Tapping opens: <span className="text-golf-orange">{actionUrl}</span>
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Send button */}
        <button
          onClick={handleSendAll}
          disabled={sending || !title.trim() || !message.trim()}
          className="w-full py-3.5 bg-golf-orange text-white rounded-xl font-semibold text-sm hover:bg-golf-orange/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 shadow-sm"
        >
          <PaperAirplaneIcon className="w-4 h-4" />
          {sending ? 'Sending...' : targetAudience === 'all' ? 'Send to All Devices' : `Send to ${TARGET_OPTIONS.find(o => o.value === targetAudience)?.label}`}
        </button>

        {/* Device Stats */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Registered Devices</h3>
            <button
              onClick={loadStats}
              disabled={loadingStats}
              className="p-1 rounded-md hover:bg-gray-100 transition-colors disabled:opacity-50"
              title="Refresh stats"
            >
              <ArrowPathIcon className={`w-3.5 h-3.5 text-gray-400 ${loadingStats ? 'animate-spin' : ''}`} />
            </button>
          </div>
          {loadingStats ? (
            <div className="animate-pulse space-y-2">
              <div className="h-4 bg-gray-200 rounded w-2/3"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          ) : stats ? (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Total</span>
                <span className="font-semibold text-gray-900">{stats.total_devices}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">iOS</span>
                <span className="font-medium text-gray-700">{stats.ios_devices}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Android</span>
                <span className="font-medium text-gray-700">{stats.android_devices}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Web</span>
                <span className="font-medium text-gray-700">{stats.web_devices}</span>
              </div>
            </div>
          ) : (
            <p className="text-xs text-gray-500">No device data available yet.</p>
          )}
        </div>

        {/* Recent History */}
        {recentNotifications.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Recent Sends</h3>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {recentNotifications.slice(0, 5).map(n => (
                <div key={n.id} className="text-xs border-b border-gray-100 pb-2 last:border-0">
                  <p className="font-medium text-gray-800 truncate">{n.title}</p>
                  <p className="text-gray-500">{n.devices_sent} devices - {new Date(n.sent_at).toLocaleDateString()}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Info */}
        <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
          <p className="text-xs text-blue-700 leading-relaxed">
            Push notifications are delivered to all users who have the mobile app installed and notifications enabled. Configure your FCM server key in Settings &gt; Mobile App.
          </p>
        </div>
      </div>
    </div>
  );
}
